import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, SimpleChanges, OnChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DxSelectBoxModule, DxDateBoxModule, DxButtonModule, DxTextBoxModule, DxValidatorModule, DxValidationGroupModule, DxValidationGroupComponent, DxTagBoxModule } from 'devextreme-angular';
import { Subject, takeUntil } from 'rxjs';
import { BuyingGroups } from '../models/buying-groups.model';
import { SlotTypes } from '../models/slot-types.model';
import { CrudService } from '../services/crud.service';
import { environment } from 'src/environments/environment';
import { weekStartDate } from '../utils/week-date.util';
import notify from 'devextreme/ui/notify';

const PROMOTION_PERIOD_NAME_MAX_LENGTH = 100;

interface PromotionType {
  promotion_type: string;
}

@Component({
  selector: 'app-create-promotion',
  imports: [CommonModule, DxSelectBoxModule, DxDateBoxModule, DxButtonModule, DxTextBoxModule, DxValidatorModule, DxValidationGroupModule, DxTagBoxModule],
  standalone: true,
  templateUrl: './create-promotion.component.html',
  styleUrl: './create-promotion.component.css'
})
export class CreatePromotionComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('validationGroup', { static: false }) validationGroup: DxValidationGroupComponent;
  @Input() buyingGroups: BuyingGroups[] = [];
  @Input() promotionTypes: PromotionType[] = [];
  @Input() allSlotTypes: SlotTypes[] = [];
  @Input() slottingBoardData: any[] = [];
  @Input() periodMode: 'week' | 'month' = 'month';
  @Input() weekStartIndex: number = 0;
  @Output() onPromotionCreated = new EventEmitter<any>();

  private apiUrl: string = environment.baseApiUrl + 'promotion-details-v2';
  private promotionNamesApiUrl: string = environment.baseApiUrl + 'promotion-names/1';
  private existingPeriodsApiUrl: string = environment.baseApiUrl + 'promotion-periods-with-bounds';

  // Alpha-sorted copy of the buyingGroups input — the select box binds to this.
  sortedBuyingGroups: BuyingGroups[] = [];
  selectedBuyingGroup: string = '';
  promotionPeriodName: string = '';
  promotionStart: Date | null = null;
  promotionEnd: Date | null = null;
  selectedPromotionType: string = '';
  description: string = '';
  slotTypes: SlotTypes[] = [];
  selectedSlotTypes: string[] = [];
  isPromotionNameValid: boolean = true;
  existingPeriods: { id: number | null; promotion_name: string; period_start_date: string | null; period_end_date: string | null }[] = [];
  selectedExistingPeriodId: number | null = null;
  isLoadingPeriods: boolean = false;

  private datePipe = new DatePipe('en-AU');
  private destroy$ = new Subject<void>();

  get isFormValid(): boolean {
    return !!this.selectedBuyingGroup &&
           !!this.promotionPeriodName?.trim() &&
           !!this.promotionStart &&
           !!this.promotionEnd &&
           !!this.selectedPromotionType &&
           this.isPromotionNameValid;
  }

  constructor(private crudService: CrudService) {}

  ngOnInit() {
    this.promotionStart = new Date();
    this.promotionEnd = this.defaultEndDate();

    this.isLoadingPeriods = true;
    this.crudService.getData(this.existingPeriodsApiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.existingPeriods = this.sortPeriodsChronologically(response?.data ?? []);
          this.isLoadingPeriods = false;
        },
        error: () => {
          this.existingPeriods = [];
          this.isLoadingPeriods = false;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private defaultEndDate(): Date {
    const endDate = new Date();
    if (this.periodMode === 'week') {
      endDate.setDate(endDate.getDate() + 14);
    } else {
      endDate.setMonth(endDate.getMonth() + 3);
    }
    return endDate;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['buyingGroups']) {
      this.sortedBuyingGroups = [...(this.buyingGroups ?? [])].sort((a, b) =>
        (a.buying_group_name ?? '').localeCompare(b.buying_group_name ?? '')
      );
    }
    // Reload slot types when slottingBoardData or allSlotTypes input changes
    if (changes['slottingBoardData'] || changes['allSlotTypes']) {
      this.filterSlotTypes();
    }
  }

  // Newest first by start date; undated periods sink to the end, name breaks ties.
  // Dates are ISO yyyy-MM-dd strings, so lexicographic order is chronological order.
  private sortPeriodsChronologically<T extends { promotion_name: string; period_start_date: string | null }>(
    periods: T[]
  ): T[] {
    return [...periods].sort((a, b) => {
      if (a.period_start_date && b.period_start_date) {
        return b.period_start_date.localeCompare(a.period_start_date)
          || (a.promotion_name ?? '').localeCompare(b.promotion_name ?? '');
      }
      if (a.period_start_date) return -1;
      if (b.period_start_date) return 1;
      return (a.promotion_name ?? '').localeCompare(b.promotion_name ?? '');
    });
  }

  // In weekly mode `slot.period.month` carries an ISO week number (1..53) — see PF-804.
  // Returns true when the slot's 7-day week interval overlaps [promoStart, promoEndExclusive).
  // Shared by filterSlotTypes and derivePromotionGroups so the weekly-overlap rule has exactly one source.
  private slotOverlapsWeeklyWindow(slot: any, promoStart: Date, promoEndExclusive: Date): boolean {
    const period = slot?.period;
    if (!period || !period.year || !period.month) {
      return false;
    }
    const ws = weekStartDate(period.year, period.month, this.weekStartIndex);
    const we = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate() + 7);
    return ws < promoEndExclusive && we > promoStart;
  }

  // Weekly only: distill the currently-scoped slotting board data down to the
  // distinct promotion-group tuples the backend needs. First slot_type_code
  // encountered per promotion_group wins (insert_promotion_details' WHERE pd.id IS NULL
  // guard makes later slot_types a no-op anyway).
  derivePromotionGroups(): Array<{ id: number; slot_type: string }> {
    if (this.periodMode !== 'week') {
      return [];
    }
    if (!this.selectedBuyingGroup || !this.promotionStart || !this.promotionEnd || !this.slottingBoardData?.length) {
      return [];
    }

    const promoStart = new Date(this.promotionStart.getFullYear(), this.promotionStart.getMonth(), this.promotionStart.getDate());
    const promoEndExclusive = new Date(this.promotionEnd.getFullYear(), this.promotionEnd.getMonth(), this.promotionEnd.getDate() + 1);
    const buyingGroupId = parseInt(this.selectedBuyingGroup, 10);
    if (Number.isNaN(buyingGroupId)) {
      return [];
    }
    const slotTypeFilter = this.selectedSlotTypes.length > 0 ? new Set(this.selectedSlotTypes) : null;

    const firstSlotTypeByGroup = new Map<number, string>();

    this.slottingBoardData.forEach((slot: any) => {
      if (slot.buying_group_id !== buyingGroupId) {
        return;
      }
      const promotionGroupId = slot.promotion_group_id;
      const slotTypeCode = slot.slot_type_code;
      if (!Number.isInteger(promotionGroupId) || promotionGroupId <= 0) {
        return;
      }
      if (typeof slotTypeCode !== 'string' || slotTypeCode.length === 0) {
        return;
      }
      if (slotTypeFilter && !slotTypeFilter.has(slotTypeCode)) {
        return;
      }
      if (!this.slotOverlapsWeeklyWindow(slot, promoStart, promoEndExclusive)) {
        return;
      }
      if (!firstSlotTypeByGroup.has(promotionGroupId)) {
        firstSlotTypeByGroup.set(promotionGroupId, slotTypeCode);
      }
    });

    return Array.from(firstSlotTypeByGroup, ([id, slot_type]) => ({ id, slot_type }));
  }

  filterSlotTypes() {
    // Only filter if we have the required values
    if (!this.selectedBuyingGroup || !this.promotionStart || !this.promotionEnd || !this.slottingBoardData?.length) {
      this.slotTypes = [];
      return;
    }

    const uniqueSlotTypeCodes = new Set<string>();

    if (this.periodMode === 'week') {
      const promoStart = new Date(this.promotionStart.getFullYear(), this.promotionStart.getMonth(), this.promotionStart.getDate());
      const promoEndExclusive = new Date(this.promotionEnd.getFullYear(), this.promotionEnd.getMonth(), this.promotionEnd.getDate() + 1);

      this.slottingBoardData.forEach((slot: any) => {
        if (slot.buying_group_id !== parseInt(this.selectedBuyingGroup)) {
          return;
        }
        if (slot.slot_type_code && this.slotOverlapsWeeklyWindow(slot, promoStart, promoEndExclusive)) {
          uniqueSlotTypeCodes.add(slot.slot_type_code);
        }
      });
    } else {
      // Monthly mode — unchanged from pre-PF-804 behaviour.
      const startYear = this.promotionStart.getFullYear();
      const startMonth = this.promotionStart.getMonth() + 1; // JS months are 0-indexed
      const endYear = this.promotionEnd.getFullYear();
      const endMonth = this.promotionEnd.getMonth() + 1;

      this.slottingBoardData.forEach((slot: any) => {
        if (slot.buying_group_id !== parseInt(this.selectedBuyingGroup)) {
          return;
        }
        const period = slot.period;
        if (period && period.year && period.month) {
          const slotDate = new Date(period.year, period.month - 1, 1);
          const startDate = new Date(startYear, startMonth - 1, 1);
          const endDate = new Date(endYear, endMonth - 1, 1);

          if (slotDate >= startDate && slotDate <= endDate && slot.slot_type_code) {
            uniqueSlotTypeCodes.add(slot.slot_type_code);
          }
        }
      });
    }

    // Filter allSlotTypes to only include those with matching codes
    this.slotTypes = this.allSlotTypes.filter(slotType =>
      uniqueSlotTypeCodes.has(slotType.code)
    );
  }

  onBuyingGroupChanged(e: any) {
    // Reset selected slot types and filter when buying group changes
    this.selectedSlotTypes = [];
    this.filterSlotTypes();
  }

  onDateRangeChanged(e: any) {
    // Reset selected slot types and filter when date range changes
    this.selectedSlotTypes = [];
    this.filterSlotTypes();
  }

  onExistingPeriodSelected(event: any) {
    const item = event?.selectedItem;
    if (!item) {
      return;
    }
    this.selectedExistingPeriodId = item.id ?? null;
    if (item.period_start_date && item.period_end_date) {
      this.promotionStart = new Date(item.period_start_date);
      this.promotionEnd = new Date(item.period_end_date);
      this.selectedSlotTypes = [];
      this.filterSlotTypes();
    }
  }

  onPeriodNameValueChanged(event: any) {
    if (!event?.event) {
      return;
    }
    const prev = this.existingPeriods.find(p => p.id === this.selectedExistingPeriodId);
    const nextValue = (event.value ?? '').toString().trim().toLowerCase();
    const prevName = (prev?.promotion_name ?? '').trim().toLowerCase();
    const matchesPrev = !!prev && nextValue === prevName;
    if (!matchesPrev) {
      this.selectedExistingPeriodId = null;
    }
  }

  onCustomPeriodCreating(event: any) {
    // Mirror the backend validation on promotion_periods.promotion_name (varchar(100))
    // so typed names fail fast in the UI rather than after the user fills the whole form.
    const cleaned = (event.text ?? '').toString().trim().slice(0, PROMOTION_PERIOD_NAME_MAX_LENGTH);
    event.customItem = {
      id: null,
      promotion_name: cleaned,
      period_start_date: null,
      period_end_date: null
    };
  }

  validatePromotionNameUnique = (e: any) => {
    return new Promise<boolean>((resolve) => {
      const value = e.value;
      if (!value || value.trim() === '') {
        this.isPromotionNameValid = true;
        resolve(true);
        return;
      }

      if (this.selectedExistingPeriodId !== null) {
        this.isPromotionNameValid = true;
        resolve(true);
        return;
      }

      const url = `${this.promotionNamesApiUrl}?promotion_period_name=${encodeURIComponent(value.trim())}`;

      this.crudService.getData(url).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: any) => {
          // If response has data, record exists - validation fails
          // If response is empty/null (204), name is unique - validation passes
          const hasData = response && (response.data || (Array.isArray(response) && response.length > 0) || (typeof response === 'object' && Object.keys(response).length > 0));
          if (hasData) {
            this.isPromotionNameValid = false;
            resolve(false);
          } else {
            this.isPromotionNameValid = true;
            resolve(true);
          }
        },
        error: (error: any) => {
          // Fail-closed: if we can't verify uniqueness we must not let the user submit,
          // otherwise a real duplicate only surfaces as an opaque backend error.
          console.error('Error checking promotion name:', error);
          this.isPromotionNameValid = false;
          resolve(false);
        }
      });
    });
  }

  createPromotion() {
    const validationResult = this.validationGroup.instance.validate();

    if (!validationResult.isValid) {
      return;
    }

    const promotionData: {
      buying_group_id: string;
      promotion_period_name: string;
      promotion_start: string;
      promotion_end: string;
      promotion_type: string;
      description: string;
      slot_type_codes: string[];
      promotion_period_id?: number;
      promotion_groups?: Array<{ id: number; slot_type: string }>;
    } = {
      buying_group_id: this.selectedBuyingGroup,
      promotion_period_name: this.promotionPeriodName,
      promotion_start: this.promotionStart
        ? (this.datePipe.transform(this.promotionStart, 'yyyy-MM-dd') ?? '')
        : '',
      promotion_end: this.promotionEnd
        ? (this.datePipe.transform(this.promotionEnd, 'yyyy-MM-dd') ?? '')
        : '',
      promotion_type: this.selectedPromotionType,
      description: this.description,
      slot_type_codes: this.selectedSlotTypes
    };

    if (this.selectedExistingPeriodId !== null) {
      promotionData.promotion_period_id = this.selectedExistingPeriodId;
    }

    if (this.periodMode === 'week') {
      promotionData.promotion_groups = this.derivePromotionGroups();
    }

    this.crudService.insertData(this.apiUrl, promotionData).subscribe({
      next: (response: any) => {
        this.resetForm();
        this.onPromotionCreated.emit(response);
      },
      error: (error: any) => {
        console.error('Error creating promotion:', error);
        // The form is deliberately NOT reset here — the user's input survives so they can
        // retry or adjust. Before this, the error was logged to the console only: a create
        // that failed server-side looked identical to nothing happening, and LiquidMix
        // "260902 The Mix Oct" was retried four times against a 500 with no feedback at all.
        notify(this.createErrorMessage(error), 'error', 6000);
      }
    });
  }

  /**
   * Surfaces the API's own message where there is one. The backend returns
   * { message, data: [...] } for both the guarded 400s (duplicate promotion, period no
   * longer available, promotion groups out of scope) and the catch-all 500, so the detail
   * in `data` is usually the most useful line — for a 500 it is the raw exception.
   */
  private createErrorMessage(error: any): string {
    const body = error?.error ?? error;
    const detail = Array.isArray(body?.data) ? body.data.filter((d: any) => typeof d === 'string')[0] : null;
    const message = typeof body?.message === 'string' ? body.message : null;

    if (message && detail) {
      return `${message}: ${detail}`;
    }
    return message ?? detail ?? 'Could not create the promotion. Please try again, or contact support if it keeps failing.';
  }

  cancel() {
    this.resetForm();
    this.onPromotionCreated.emit(null);
  }

  private resetForm() {
    // DX validation-group reset clears bound editor values to null — run it first so
    // the defaults we assign below survive.
    this.validationGroup?.instance.reset();
    this.selectedBuyingGroup = '';
    this.promotionPeriodName = '';
    this.promotionStart = new Date();
    this.promotionEnd = this.defaultEndDate();
    this.selectedPromotionType = '';
    this.description = '';
    this.selectedSlotTypes = [];
    this.slotTypes = [];
    this.isPromotionNameValid = true;
    this.selectedExistingPeriodId = null;
  }
}
